/**
 * Terminal Page - Simplified PDV for restaurant floor employees
 * Features: Employee login, table/order selection, add products, view consumed items
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useInstance } from '../../contexts/InstanceContext';
import { ordersAPI, productsAPI, categoriesAPI, tablesAPI, authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Monitor,
  User,
  Lock,
  Table2,
  LogOut,
  Plus,
  Minus,
  Package,
  ShoppingCart,
  ChevronLeft,
  Search,
  Check,
  Coffee,
  Utensils,
  Wine,
  IceCream,
  Soup,
  Pizza,
  Salad,
  Sandwich,
  Cookie,
  Fish,
  Beef,
  Egg,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import './Terminal.css';

// Icon map for categories
const iconMap = {
  Coffee, Utensils, Wine, IceCream, Soup, Pizza, Salad, 
  Sandwich, Cookie, Fish, Beef, Egg, Package,
};

// Terminal States
const STATES = {
  LOGIN: 'login',
  SELECT_TABLE: 'select_table',
  PRODUCTS: 'products',
};

export default function Terminal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentInstance } = useInstance();
  
  // Terminal state
  const [terminalState, setTerminalState] = useState(STATES.LOGIN);
  const [employee, setEmployee] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  
  // Data
  const [tables, setTables] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loginCode, setLoginCode] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCart, setShowCart] = useState(false);
  
  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currentInstance?.settings?.currency || 'BRL',
    }).format(value || 0);
  };

  // Load tables, products and categories
  const loadData = useCallback(async () => {
    try {
      const [tablesRes, productsRes, categoriesRes] = await Promise.all([
        tablesAPI.getAll(),
        productsAPI.getAll(),
        categoriesAPI.getAll(),
      ]);
      
      // Handle different response formats (array or {data: array} or {success: true, data: array})
      const extractData = (res) => {
        if (Array.isArray(res.data)) return res.data;
        if (res.data?.data) return res.data.data;
        return [];
      };
      
      setTables(extractData(tablesRes));
      setProducts(extractData(productsRes));
      setCategories(extractData(categoriesRes));
    } catch (err) {
      console.error('Error loading data:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Employee login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginCode || !loginPassword) {
      toast.error('Preencha código e senha');
      return;
    }

    try {
      setLoading(true);
      
      // Try to authenticate with employee credentials
      const response = await authAPI.login({
        email: loginCode,  // Can be email or employee code
        password: loginPassword,
      });
      
      if (response.data.token) {
        // Store token for API calls (same key as main app uses)
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        setEmployee(response.data.user);
        setTerminalState(STATES.SELECT_TABLE);
        toast.success(`Bem-vindo, ${response.data.user.name}!`);
        
        // Reload data with authenticated token
        await loadData();
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error('Código ou senha inválidos');
    } finally {
      setLoading(false);
      setLoginPassword('');
    }
  };

  // Handle table selection
  const handleSelectTable = async (table) => {
    setSelectedTable(table);
    
    try {
      setLoading(true);
      
      // Check if table has an open order
      const ordersRes = await ordersAPI.getAll({ table_id: table.id, status: 'open' });
      const ordersData = Array.isArray(ordersRes.data) ? ordersRes.data : (ordersRes.data?.data || []);
      const openOrders = ordersData.filter(o => o.status === 'open' && o.table_id === table.id);
      
      if (openOrders.length > 0) {
        // Load existing order
        setCurrentOrder(openOrders[0]);
        toast.info(`Pedido existente carregado - ${openOrders[0].items?.length || 0} itens`);
      } else {
        // Create new order for this table
        const newOrderRes = await ordersAPI.create({
          table_id: table.id,
          table_number: table.number,
          items: [],
          status: 'open',
          user_id: employee?.id,
          user_name: employee?.name,
        });
        
        const newOrderData = newOrderRes.data?.data || newOrderRes.data;
        if (newOrderData?.id) {
          setCurrentOrder({
            id: newOrderData.id,
            table_id: table.id,
            table_number: table.number,
            items: [],
            status: 'open',
          });
          toast.success('Novo pedido criado');
        }
      }
      
      setTerminalState(STATES.PRODUCTS);
    } catch (err) {
      console.error('Error selecting table:', err);
      toast.error('Erro ao selecionar mesa');
    } finally {
      setLoading(false);
    }
  };

  // Add item to order
  const addToOrder = async (product) => {
    if (!currentOrder) return;
    
    try {
      const existingItem = currentOrder.items?.find(i => i.product_id === product.id);
      let updatedItems;
      
      if (existingItem) {
        updatedItems = currentOrder.items.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        const newItem = {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          price: product.price,
          notes: '',
        };
        updatedItems = [...(currentOrder.items || []), newItem];
      }
      
      // Update order on server
      await ordersAPI.update(currentOrder.id, { items: updatedItems });
      
      setCurrentOrder(prev => ({ ...prev, items: updatedItems }));
      toast.success(`${product.name} adicionado`);
    } catch (err) {
      console.error('Error adding item:', err);
      toast.error('Erro ao adicionar item');
    }
  };

  // Remove item from order
  const removeFromOrder = async (productId) => {
    if (!currentOrder) return;
    
    try {
      const existingItem = currentOrder.items?.find(i => i.product_id === productId);
      if (!existingItem) return;
      
      let updatedItems;
      if (existingItem.quantity > 1) {
        updatedItems = currentOrder.items.map(item =>
          item.product_id === productId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      } else {
        updatedItems = currentOrder.items.filter(i => i.product_id !== productId);
      }
      
      await ordersAPI.update(currentOrder.id, { items: updatedItems });
      setCurrentOrder(prev => ({ ...prev, items: updatedItems }));
    } catch (err) {
      console.error('Error removing item:', err);
      toast.error('Erro ao remover item');
    }
  };

  // Calculate order total
  const orderTotal = useMemo(() => {
    if (!currentOrder?.items) return 0;
    return currentOrder.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [currentOrder?.items]);

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (!p.is_active) return false;
      if (selectedCategory !== 'all' && p.category_id !== selectedCategory) return false;
      if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [products, selectedCategory, searchQuery]);

  // Parent categories only
  const parentCategories = useMemo(() => {
    return categories.filter(c => c.is_active && !c.parent_id);
  }, [categories]);

  // Logout
  const handleLogout = () => {
    // Clear terminal session tokens
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    setEmployee(null);
    setSelectedTable(null);
    setCurrentOrder(null);
    setTerminalState(STATES.LOGIN);
    setLoginCode('');
    setLoginPassword('');
    setTables([]);
    setProducts([]);
    setCategories([]);
  };

  // Go back
  const goBack = () => {
    if (terminalState === STATES.PRODUCTS) {
      setTerminalState(STATES.SELECT_TABLE);
      setSelectedTable(null);
      setCurrentOrder(null);
    } else if (terminalState === STATES.SELECT_TABLE) {
      handleLogout();
    }
  };

  // Render Login Screen
  if (terminalState === STATES.LOGIN) {
    return (
      <div className="terminal-login">
        <div className="terminal-login-card">
          <div className="terminal-login-header">
            <Monitor className="h-12 w-12 text-primary" />
            <h1>Terminal</h1>
            <p>{currentInstance?.name || 'SisPDV'}</p>
          </div>
          
          <form onSubmit={handleLogin} className="terminal-login-form">
            <div className="space-y-2">
              <Label htmlFor="code">Código / Email</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="code"
                  type="text"
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value)}
                  placeholder="Seu código ou email"
                  className="pl-10"
                  autoFocus
                  data-testid="terminal-code-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Sua senha"
                  className="pl-10"
                  data-testid="terminal-password-input"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 text-lg" 
              disabled={loading}
              data-testid="terminal-login-btn"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
          
          <Button
            variant="ghost"
            className="w-full mt-4"
            onClick={() => navigate('/')}
          >
            Voltar ao Sistema Principal
          </Button>
        </div>
      </div>
    );
  }

  // Render Table Selection Screen
  if (terminalState === STATES.SELECT_TABLE) {
    return (
      <div className="terminal-tables">
        <header className="terminal-header">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1>Selecionar Mesa</h1>
            <p className="text-sm text-muted-foreground">
              Olá, {employee?.name}
            </p>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sair
          </Button>
        </header>

        <div className="terminal-tables-grid">
          {tables.filter(t => t.is_active).map(table => (
            <button
              key={table.id}
              onClick={() => handleSelectTable(table)}
              className={cn(
                'terminal-table-btn',
                table.status === 'occupied' && 'occupied',
                table.status === 'reserved' && 'reserved'
              )}
              data-testid={`terminal-table-${table.id}`}
            >
              <Table2 className="h-8 w-8" />
              <span className="text-2xl font-bold">Mesa {table.number}</span>
              <Badge variant={table.status === 'available' ? 'secondary' : 'default'}>
                {table.status === 'available' ? 'Livre' : 
                 table.status === 'occupied' ? 'Ocupada' : 'Reservada'}
              </Badge>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Render Products Screen
  return (
    <div className="terminal-products">
      {/* Header */}
      <header className="terminal-header">
        <Button variant="ghost" size="icon" onClick={goBack}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1>Mesa {selectedTable?.number}</h1>
          <p className="text-sm text-muted-foreground">
            {employee?.name} • {currentOrder?.items?.length || 0} itens
          </p>
        </div>
        <Button 
          variant="default"
          onClick={() => setShowCart(true)}
          className="gap-2"
          data-testid="terminal-cart-btn"
        >
          <ShoppingCart className="h-4 w-4" />
          {formatCurrency(orderTotal)}
        </Button>
      </header>

      <div className="terminal-content">
        {/* Categories */}
        <aside className="terminal-categories">
          <button
            onClick={() => setSelectedCategory('all')}
            className={cn('terminal-category-btn', selectedCategory === 'all' && 'active')}
          >
            <Package className="h-5 w-5" />
            Todos
          </button>
          {parentCategories.map(cat => {
            const Icon = iconMap[cat.icon] || Package;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={cn('terminal-category-btn', selectedCategory === cat.id && 'active')}
              >
                <Icon className="h-5 w-5" />
                {cat.name}
              </button>
            );
          })}
        </aside>

        {/* Products Grid */}
        <main className="terminal-main">
          {/* Search */}
          <div className="terminal-search">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar produto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 bg-transparent focus-visible:ring-0"
            />
          </div>

          {/* Products */}
          <ScrollArea className="terminal-products-scroll">
            <div className="terminal-products-grid">
              {filteredProducts.map(product => (
                <button
                  key={product.id}
                  onClick={() => addToOrder(product)}
                  className="terminal-product-card"
                  data-testid={`terminal-product-${product.id}`}
                >
                  <div className="terminal-product-name">{product.name}</div>
                  <div className="terminal-product-price">{formatCurrency(product.price)}</div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </main>
      </div>

      {/* Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Itens da Mesa {selectedTable?.number}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            {currentOrder?.items?.length > 0 ? (
              <div className="space-y-2">
                {currentOrder.items.map((item, idx) => (
                  <div key={`${item.product_id}-${idx}`} className="terminal-cart-item">
                    <div className="flex-1">
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(item.price)} cada
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removeFromOrder(item.product_id)}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-mono font-bold">
                        {item.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => addToOrder(products.find(p => p.id === item.product_id))}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="w-24 text-right font-mono font-semibold">
                      {formatCurrency(item.price * item.quantity)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-muted-foreground">
                <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum item adicionado</p>
              </div>
            )}
          </ScrollArea>
          
          <Separator />
          
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-2xl font-mono font-bold text-primary">
              {formatCurrency(orderTotal)}
            </span>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowCart(false)}>
              Continuar
            </Button>
            <Button 
              className="flex-1" 
              onClick={() => { setShowCart(false); goBack(); }}
            >
              <Check className="h-4 w-4 mr-2" />
              Finalizar Mesa
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
